/**
 * App.tsx
 *
 * Main App component for Via Logos web application
 * Converted from react-native-web to standard React
 */
import React, { useState, useEffect } from 'react';
import { ThemeProvider, useTheme } from './services/ThemeContext';
import { SettingsProvider, useSettings } from './services/SettingsContext';
import { FontProvider } from './services/FontProvider';
import { AchievementsProvider, useAchievements } from './services/AchievementsContext';

// These components will need to be converted from react-native-web
// For now, we'll create placeholder components
import { GameState, Message, AIHistoryMessage } from './constants/types';
import { INITIAL_PROMPT, DEBUG, isWordStudyTopic, getRandomLoadingMessage } from './constants/variables';
import { fetchAIResponse, generateSceneImage, summarizeConversation } from './services/aiService';
import {
  importGameState,
  exportGameState,
  saveAutosave,
  loadAutosave,
  listAutosaves,
  deleteAutosave,
} from './services/storageService';
import logger from './services/logger';
import DonationModal from './components/UI/DonationModal';
import SettingsMenu from './components/UI/SettingsMenu';
import IntroScreen from './components/UI/IntroScreen';
import SetupWizard from './components/UI/SetupWizard.web';
import NarrativeUI from './components/UI/NarrativeUI.web';
import viaLogosBackground from '/assets/via_logos_background.png';

// Placeholder UI components (to be converted)
// REMOVED: Inline components replaced by external imports - Jan 2026
function _IntroScreen({ onBegin, onDonate }: { onBegin: () => void; onDonate: () => void }) {
  const { currentTheme: colors } = useTheme();

  // Helper to darken color by percentage (matching mobile)
  const darkenColor = (color: string, percent: number): string => {
    let hex = color.replace('#', '');
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
    r = Math.floor((r * (100 - percent)) / 100);
    g = Math.floor((g * (100 - percent)) / 100);
    b = Math.floor((b * (100 - percent)) / 100);
    const toHex = (n: number) => n.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  // Mobile-sized container wrapper
  return (
    <div style={{ display: 'flex', flexDirection: 'row', flex: 1, backgroundColor: '#000', minHeight: '100vh' }}>
      <div style={{ flex: 1, maxWidth: 500, width: '100%', margin: '0 auto', position: 'relative', minHeight: '100vh' }}>
        {/* Background Image */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${viaLogosBackground})`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.6,
            zIndex: 0,
          }}
        />

        {/* Dark Overlay */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: colors.background,
            opacity: 0.3,
            zIndex: 1,
          }}
        />

        {/* Help icon - top right */}
        <div
          onClick={onDonate}
          style={{
            position: 'absolute',
            top: 20,
            right: 20,
            width: 28,
            height: 28,
            borderRadius: 14,
            borderWidth: 2,
            borderColor: colors.primary,
            borderStyle: 'solid',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3,
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 'bold', fontStyle: 'italic', color: colors.primary }}>?</span>
        </div>

        {/* Title and Hook - positioned at 20% from top */}
        <div
          style={{
            position: 'absolute',
            top: '20%',
            left: 0,
            right: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '0 20px',
            zIndex: 4,
          }}
        >
          <h1
            className="dream-catcher-title fantasy-font"
            style={{
              fontFamily: "'Eagle Lake', serif",
              fontWeight: '400',
              fontStyle: 'normal',
              textAlign: 'center',
              textShadow: '4px -1.5px 6px #d4af37',
              marginBottom: 12,
              color: '#4a90e2',
              fontSize: 60,
              margin: '0 0 12px 0',
            }}
          >
            Via Logos
          </h1>

          <p
            className="dream-catcher-title fantasy-font"
            style={{
              fontFamily: "'Eagle Lake', serif",
              fontWeight: '400',
              fontStyle: 'italic',
              textAlign: 'center',
              textShadow: '2px 2px 4px #999999',
              color: '#e0e0e0',
              fontSize: 20,
              margin: 0,
            }}
          >
            Walk through the living Word.
          </p>
        </div>

        {/* Begin Button - stays at bottom */}
        <div
          style={{
            position: 'absolute',
            bottom: 60,
            left: 0,
            right: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '0 20px',
            zIndex: 4,
          }}
        >
          <div style={{ width: '80%', maxWidth: 400 }}>
            <button
              onClick={onBegin}
              style={{
                width: '100%',
                padding: '18px 40px',
                borderRadius: 30,
                backgroundColor: darkenColor(colors.primary, 20),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <span
                style={{
                  color: '#ffffff',
                  fontWeight: 'bold',
                  fontSize: 18,
                  fontFamily: "'Eagle Lake', serif",
                }}
              >
                Begin
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function _SetupWizard({
  onStart,
  onImport,
  autosaves,
  onLoadGame,
  onClose,
}: {
  onStart: (state: GameState) => void;
  onImport: () => void;
  autosaves: any[];
  onLoadGame: (id: string) => void;
  onClose: () => void;
}) {
  const { currentTheme: colors } = useTheme();

  // State matching mobile
  const [mode, setMode] = useState<'immersive' | 'examine'>('immersive');
  const [storyName, setStoryName] = useState('');
  const [bibleVerse, setBibleVerse] = useState('');
  const [verseFocus, setVerseFocus] = useState('');
  const [showExistingStories, setShowExistingStories] = useState(false);

  const handleStart = () => {
    if (!bibleVerse.trim()) {
      alert('Please enter a Bible verse or topic');
      return;
    }
    onStart({
      mode,
      storyName,
      bibleVerse,
      bibleVerseText: '', // Will be populated by AI
      customDirectives: verseFocus,
      status: 'playing',
      history: [],
      turnNumber: 0,
      id: '',
      theme: '',
    });
  };

  const handleDeleteAutosave = (id: string) => {
    // TODO: Implement delete functionality
    console.log('Delete autosave:', id);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'row', flex: 1, backgroundColor: '#000', minHeight: '100vh' }}>
      <div style={{ flex: 1, maxWidth: 500, width: '100%', margin: '0 auto', position: 'relative', minHeight: '100vh' }}>
        {/* Background Image */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${viaLogosBackground})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.6,
            zIndex: 0,
          }}
        />

        {/* Close button */}
        {onClose && (
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: 20,
              left: 20,
              width: 40,
              height: 40,
              borderRadius: 20,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              border: 'none',
              color: colors.primary,
              fontSize: 24,
              fontWeight: 'bold',
              cursor: 'pointer',
              zIndex: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ✕
          </button>
        )}

        {/* Scrollable Content */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            padding: '40px 20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minHeight: '100vh',
          }}
        >
          {/* Glass Card Container */}
          <div
            style={{
              width: '90%',
              maxWidth: 600,
              padding: 32,
              borderRadius: 16,
              borderWidth: 1,
              borderColor: colors.border,
              backgroundColor: `rgba(30, 41, 59, 0.7)`,
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              boxShadow: '0 10px 20px rgba(0, 0, 0, 0.3)',
            }}
          >
            {/* Existing Stories Panel */}
            {autosaves && autosaves.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <button
                  onClick={() => setShowExistingStories(!showExistingStories)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 12,
                    backgroundColor: `${colors.background}40`,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: colors.border,
                    borderStyle: 'solid',
                    color: colors.textMain,
                    fontSize: 14,
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  <span>
                    Continue Existing Passage{autosaves.length === 1 ? '' : 's'} ({autosaves.length})
                  </span>
                  <span>{showExistingStories ? '▼' : '▶'}</span>
                </button>

                {showExistingStories && (
                  <div
                    style={{
                      marginTop: 10,
                      borderWidth: 1,
                      borderColor: colors.border,
                      borderStyle: 'solid',
                      borderRadius: 8,
                      backgroundColor: `${colors.background}20`,
                      maxHeight: 300,
                    }}
                  >
                    <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                      {autosaves.map((save: any) => (
                        <div
                          key={save.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: 12,
                            borderBottomWidth: 1,
                            borderBottomColor: `${colors.border}50`,
                            borderBottomStyle: 'solid',
                          }}
                        >
                          <button
                            onClick={() => onLoadGame && onLoadGame(save.id)}
                            style={{
                              flex: 1,
                              background: 'none',
                              border: 'none',
                              color: colors.textMain,
                              fontSize: 14,
                              fontWeight: '600',
                              textAlign: 'left',
                              cursor: 'pointer',
                              padding: 0,
                            }}
                          >
                            <div>{save.name}</div>
                            <div style={{ fontSize: 12, color: colors.textMuted }}>
                              {save.bibleVerse}
                            </div>
                          </button>
                          <button
                            onClick={() => handleDeleteAutosave(save.id)}
                            style={{
                              marginLeft: 12,
                              padding: 8,
                              background: 'none',
                              border: 'none',
                              color: '#ef4444',
                              fontSize: 18,
                              fontWeight: 'bold',
                              cursor: 'pointer',
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Empty state when no passages */}
            {(!autosaves || autosaves.length === 0) && (
              <div
                style={{
                  marginBottom: 20,
                  padding: 20,
                  backgroundColor: `${colors.background}20`,
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: colors.border,
                  borderStyle: 'solid',
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    color: colors.textMuted,
                    fontSize: 14,
                    fontStyle: 'italic',
                    textAlign: 'center',
                  }}
                >
                  Begin a Bible verse walkthrough experience.
                </div>
              </div>
            )}

            {/* Story Name (Optional) */}
            <div style={{ marginBottom: 24 }}>
              <label
                style={{
                  display: 'block',
                  fontSize: 10,
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                  marginBottom: 8,
                  color: colors.textMuted,
                }}
              >
                Story Name (Optional)
              </label>
              <input
                type="text"
                value={storyName}
                onChange={e => setStoryName(e.target.value)}
                placeholder="Defaults to verse reference"
                style={{
                  width: '100%',
                  borderWidth: 1,
                  borderColor: colors.border,
                  borderStyle: 'solid',
                  borderRadius: 8,
                  paddingLeft: 16,
                  paddingRight: 16,
                  paddingTop: 10,
                  paddingBottom: 10,
                  fontSize: 14,
                  backgroundColor: colors.surface,
                  color: colors.textMain,
                }}
              />
            </div>

            {/* Passage or Topic Reference (Required) */}
            <div style={{ marginBottom: 24 }}>
              <label
                style={{
                  display: 'block',
                  fontSize: 10,
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                  marginBottom: 8,
                  color: colors.textMuted,
                }}
              >
                Passage or Topic *
              </label>
              <input
                type="text"
                value={bibleVerse}
                onChange={e => setBibleVerse(e.target.value)}
                placeholder="e.g., John 3:16 or Love"
                style={{
                  width: '100%',
                  borderWidth: 1,
                  borderColor: colors.border,
                  borderStyle: 'solid',
                  borderRadius: 8,
                  paddingLeft: 16,
                  paddingRight: 16,
                  paddingTop: 10,
                  paddingBottom: 10,
                  fontSize: 14,
                  backgroundColor: colors.surface,
                  color: colors.textMain,
                }}
              />
            </div>

            {/* Display Mode */}
            <div style={{ marginBottom: 24 }}>
              <label
                style={{
                  display: 'block',
                  fontSize: 10,
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                  marginBottom: 8,
                  color: colors.textMuted,
                }}
              >
                Display Mode
              </label>
              <div
                style={{
                  display: 'flex',
                  borderRadius: 8,
                  overflow: 'hidden',
                  borderWidth: 1,
                  borderColor: colors.border,
                  borderStyle: 'solid',
                }}
              >
                <button
                  onClick={() => setMode('immersive')}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    backgroundColor: mode === 'immersive' ? colors.primary : colors.surface,
                    border: 'none',
                    color: mode === 'immersive' ? '#ffffff' : colors.textMain,
                    fontSize: 14,
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Immersive Story
                </button>
                <button
                  onClick={() => setMode('examine')}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    backgroundColor: mode === 'examine' ? colors.primary : colors.surface,
                    border: 'none',
                    color: mode === 'examine' ? '#ffffff' : colors.textMain,
                    fontSize: 14,
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Examine & Study
                </button>
              </div>
            </div>

            {/* Verse Focus (Optional) */}
            <div style={{ marginBottom: 24 }}>
              <label
                style={{
                  display: 'block',
                  fontSize: 10,
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                  marginBottom: 8,
                  color: colors.textMuted,
                }}
              >
                Verse Focus (Optional)
              </label>
              <textarea
                value={verseFocus}
                onChange={e => setVerseFocus(e.target.value)}
                rows={2}
                placeholder="What aspects of this verse would you like to explore?"
                style={{
                  width: '100%',
                  borderWidth: 1,
                  borderColor: colors.border,
                  borderStyle: 'solid',
                  borderRadius: 8,
                  padding: '8px 12px',
                  fontSize: 14,
                  backgroundColor: colors.surface,
                  color: colors.textMain,
                  minHeight: 56,
                  resize: 'vertical',
                }}
              />
            </div>

            {/* Disclaimer */}
            <div style={{ marginTop: 16, marginBottom: 8, paddingLeft: 16,
                  paddingRight: 16 }}>
              <div
                style={{
                  color: colors.textMuted,
                  fontSize: 14,
                  fontStyle: 'italic',
                  textAlign: 'center',
                  lineHeight: '20px',
                }}
              >
                Note: The generated stories draw from historical data and conservative biblical
                commentaries. Interpretations beyond the actual verse text may vary and should
                be studied further for deeper understanding.
              </div>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 16, marginTop: 24 }}>
              <button
                onClick={handleStart}
                style={{
                  flex: 1,
                  padding: '12px 14px',
                  borderRadius: 10,
                  backgroundColor: colors.primary,
                  border: 'none',
                  color: '#ffffff',
                  fontSize: 14,
                  fontWeight: '600',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                Begin Walkthrough
              </button>
              <button
                onClick={onImport}
                style={{
                  flex: 1,
                  padding: '12px 14px',
                  borderRadius: 10,
                  backgroundColor: 'transparent',
                  borderWidth: 1,
                  borderColor: colors.border,
                  borderStyle: 'solid',
                  color: colors.textMain,
                  fontSize: 14,
                  fontWeight: '600',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                Load Walkthrough
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function _NarrativeUI({
  gameState,
  onSendMessage,
  onSilentContinue,
  onRedo,
  onExit,
  isLoading,
  onDonate,
  onOpenSettings,
}: {
  gameState: GameState;
  onSendMessage: (text: string) => void;
  onSilentContinue: () => void;
  onRedo: () => void;
  onExit: () => void;
  isLoading: boolean;
  onDonate: () => void;
  onOpenSettings: () => void;
}) {
  const { currentTheme: colors } = useTheme();
  const [inputText, setInputText] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [showChoicesDropdown, setShowChoicesDropdown] = useState(false);
  const [showPassagesDropdown, setShowPassagesDropdown] = useState(false);

  // Auto-scroll to bottom when new messages arrive
  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [gameState.history.length, isLoading]);

  const lastMessage = gameState.history.length > 0 ? gameState.history[gameState.history.length - 1] : undefined;
  const actions = (lastMessage?.suggestedActions || []).slice(0, 2);
  const relatedPassages = lastMessage?.relatedPassages || [];
  const choicesWithAsk = [...actions, '❓ Ask what this means'];

  // Check if current topic is a word study (only show Passages button for word studies)
  const isWordStudy = isWordStudyTopic(gameState.bibleVerse);

  const handleContinue = () => {
    const trimmed = inputText.trim();
    if (trimmed) {
      onSendMessage(trimmed);
    } else {
      onSilentContinue();
    }
    setInputText('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleContinue();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'row', flex: 1, backgroundColor: '#000', minHeight: '100vh' }}>
      <div style={{ flex: 1, maxWidth: 500, width: '100%', margin: '0 auto', position: 'relative', minHeight: '100vh' }}>
        {/* Background image layer */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: gameState.backgroundImage
              ? `url(${gameState.backgroundImage})`
              : `url(${viaLogosBackground})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: 0,
          }}
        />

        {/* Semi-transparent overlay to darken the background */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
            zIndex: 1,
          }}
        />

        {/* Main content container */}
        <div
          style={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
          }}
        >
          {/* Header Row - Menu, Title, Settings */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingLeft: 16,
                  paddingRight: 16,
              paddingTop: 12,
                  paddingBottom: 12,
              backgroundColor: colors.surface,
              borderBottomWidth: 1,
              borderBottomColor: colors.border,
              borderBottomStyle: 'solid',
              flexShrink: 0,
              marginTop: 15,
            }}
          >
            {/* Menu Button - Left */}
            <button
              onClick={() => setSidebarOpen(true)}
              style={{ background: 'none', border: 'none', padding: 8, fontSize: 20, color: colors.primary, cursor: 'pointer' }}
            >
              ☰
            </button>

            {/* Title - Center */}
            <span
              style={{
                fontSize: 18,
                fontWeight: 'bold',
                padding: '8px 12px',
                color: colors.primary,
                letterSpacing: 1,
              }}
            >
              {gameState.storyName || gameState.bibleVerse || 'Passage'}
            </span>

            {/* Settings/Donate - Right */}
            <button
              onClick={onOpenSettings}
              style={{ background: 'none', border: 'none', padding: 8, fontSize: 28, color: colors.primary, cursor: 'pointer' }}
            >
              ⚙
            </button>
          </div>

          {/* Messages Scroll Area */}
          <div
            style={{
              flex: 1,
              paddingTop: 10,
              paddingBottom: 10,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              ref={scrollRef}
              style={{
                flex: 1,
                overflowY: 'auto',
              }}
            >
            {gameState.history.map((m, i) => (
              <div
                key={m.id || i}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  paddingLeft: 16,
                  paddingRight: 16,
                  paddingTop: 8,
                  paddingBottom: 8,
                  alignItems: m.role === 'user' ? 'flex-end' : 'center',
                }}
              >
                <div
                  style={{
                    maxWidth: '80%',
                    padding: 12,
                    borderRadius: 16,
                    backgroundColor: m.role === 'user' ? `${colors.primary}1A` : 'transparent',
                  }}
                >
                  <div style={{ fontSize: 15, lineHeight: '22px', color: colors.textMain, fontWeight: m.role === 'user' ? '400' : '500' }}>
                    {m.content}
                  </div>
                  {m.imageUrl && (
                    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <img
                        src={m.imageUrl}
                        alt="Scene"
                        style={{ width: '100%', maxWidth: 320, borderRadius: 8 }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginLeft: 20, marginTop: 8 }}>
                <div style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  border: `2px solid ${colors.textMuted}`,
                  borderTopColor: 'transparent',
                  animation: 'spin 1s linear infinite',
                  padding: 4,
                }} />
                <span style={{ color: colors.textMuted, fontStyle: 'italic', marginLeft: 16, fontSize: 14 }}>
                  {loadingMessage}
                </span>
              </div>
            )}
            </div>
          </div>

          {/* Footer with dropdowns and input */}
          <div
            style={{
              position: 'relative',
              backgroundColor: `${colors.background}E6`,
              borderTopWidth: 1,
              borderTopColor: colors.border,
              borderTopStyle: 'solid',
              paddingLeft: 16,
                  paddingRight: 16,
              paddingTop: 12,
                  paddingBottom: 12,
              flexShrink: 0,
              marginBottom: 15,
            }}
          >
            {/* Dropdowns Row - Related Passages, Choices, and Redo */}
            {!isLoading && (choicesWithAsk.length > 0 || (isWordStudy && relatedPassages.length > 0)) && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginBottom: 12,
                  gap: 8,
                }}
              >
                {/* Related Passages Dropdown Button - ONLY for word studies */}
                {isWordStudy && relatedPassages.length > 0 && (
                  <button
                    onClick={() => {
                      setShowChoicesDropdown(false);
                      setShowPassagesDropdown(!showPassagesDropdown);
                    }}
                    style={{
                      flex: 1,
                      backgroundColor: colors.surface,
                      borderWidth: 1,
                      borderColor: colors.primary,
                      borderStyle: 'solid',
                      borderRadius: 8,
                      padding: '10px 12px',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ color: colors.textMain, fontSize: 14 }}>
                      Extra Passages
                    </span>
                    <span style={{ color: colors.primary, fontSize: 12 }}>▲</span>
                  </button>
                )}

                {/* Choices Dropdown Button */}
                {choicesWithAsk.length > 0 && (
                  <button
                    onClick={() => {
                      setShowPassagesDropdown(false);
                      setShowChoicesDropdown(!showChoicesDropdown);
                    }}
                    style={{
                      flex: 1,
                      backgroundColor: colors.primary,
                      borderWidth: 1,
                      borderColor: colors.primary,
                      borderStyle: 'solid',
                      borderRadius: 8,
                      padding: '10px 12px',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ color: colors.background, fontSize: 14 }}>
                      Choices ({choicesWithAsk.length})
                    </span>
                    <span style={{ color: colors.background, fontSize: 12 }}>▲</span>
                  </button>
                )}

                {/* Redo Button */}
                <button
                  onClick={onRedo}
                  disabled={isLoading || gameState.history.length === 0}
                  style={{
                    backgroundColor: colors.surface,
                    borderWidth: 1,
                    borderColor: colors.border,
                    borderStyle: 'solid',
                    borderRadius: 8,
                    padding: '10px 12px',
                    cursor: isLoading || gameState.history.length === 0 ? 'not-allowed' : 'pointer',
                    opacity: isLoading || gameState.history.length === 0 ? 0.5 : 1,
                  }}
                >
                  <span style={{ color: colors.textMain, fontSize: 13 }}>↺ Redo</span>
                </button>
              </div>
            )}

            {/* Text Input Area with Continue Button */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-end',
                gap: 8,
                backgroundColor: colors.background,
                borderWidth: 1,
                borderColor: colors.border,
                borderStyle: 'solid',
                borderRadius: 8,
                padding: 8,
              }}
            >
              <textarea
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="What do you do? (blank to continue)"
                disabled={isLoading}
                style={{
                  flex: 1,
                  background: 'none',
                  border: 'none',
                  color: colors.textMain,
                  fontSize: 15,
                  resize: 'none',
                  outline: 'none',
                  minHeight: 40,
                  maxHeight: 120,
                  fontFamily: 'inherit',
                }}
                rows={1}
              />

              {/* Continue Button */}
              <button
                onClick={handleContinue}
                disabled={isLoading}
                style={{
                  backgroundColor: colors.primary,
                  borderWidth: 1,
                  borderColor: colors.primary,
                  borderStyle: 'solid',
                  borderRadius: 8,
                  padding: '10px 16px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.5 : 1,
                }}
              >
                <span style={{ color: colors.background, fontSize: 14, fontWeight: '600' }}>
                  Continue
                </span>
              </button>
            </div>

            {/* Passages Dropdown */}
            {!isLoading && showPassagesDropdown && relatedPassages.length > 0 && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 100,
                  left: 16,
                  right: 16,
                  backgroundColor: colors.surface,
                  borderWidth: 1,
                  borderColor: colors.primary,
                  borderStyle: 'solid',
                  borderRadius: 8,
                  maxHeight: 200,
                  zIndex: 2000,
                  boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingLeft: 12,
                  paddingRight: 12,
                    paddingTop: 10,
                  paddingBottom: 10,
                    borderBottomWidth: 1,
                    borderBottomColor: colors.border,
                    borderBottomStyle: 'solid',
                  }}
                >
                  <span style={{ color: colors.primary, fontSize: 14, fontWeight: '600' }}>
                    Related Passages
                  </span>
                  <button
                    onClick={() => setShowPassagesDropdown(false)}
                    style={{ background: 'none', border: 'none', color: colors.primary, fontSize: 18, cursor: 'pointer' }}
                  >
                    ✕
                  </button>
                </div>
                <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                  {relatedPassages.map((passage, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setShowPassagesDropdown(false);
                        onSendMessage(`Explore ${passage}`);
                      }}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        paddingLeft: 12,
                  paddingRight: 12,
                        paddingTop: 10,
                  paddingBottom: 10,
                        borderBottomWidth: index < relatedPassages.length - 1 ? 1 : 0,
                        borderBottomColor: colors.border,
                        borderBottomStyle: 'solid',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{ color: colors.textMain, fontSize: 14 }}>{passage}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Choices Dropdown */}
            {!isLoading && showChoicesDropdown && choicesWithAsk.length > 0 && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 100,
                  left: 16,
                  right: 16,
                  backgroundColor: colors.surface,
                  borderWidth: 1,
                  borderColor: colors.primary,
                  borderStyle: 'solid',
                  borderRadius: 8,
                  maxHeight: 200,
                  zIndex: 2000,
                  boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingLeft: 12,
                  paddingRight: 12,
                    paddingTop: 10,
                  paddingBottom: 10,
                    borderBottomWidth: 1,
                    borderBottomColor: colors.border,
                    borderBottomStyle: 'solid',
                  }}
                >
                  <span style={{ color: colors.primary, fontSize: 14, fontWeight: '600' }}>
                    Choices
                  </span>
                  <button
                    onClick={() => setShowChoicesDropdown(false)}
                    style={{ background: 'none', border: 'none', color: colors.primary, fontSize: 18, cursor: 'pointer' }}
                  >
                    ✕
                  </button>
                </div>
                <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                  {choicesWithAsk.map((choice, index) => {
                    const isAsk = choice.startsWith('❓');
                    return (
                      <button
                        key={index}
                        onClick={() => {
                          setShowChoicesDropdown(false);
                          if (isAsk) {
                            onSendMessage('What does this mean?');
                          } else {
                            onSendMessage(choice);
                          }
                        }}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          paddingLeft: 12,
                  paddingRight: 12,
                          paddingTop: 10,
                  paddingBottom: 10,
                          borderBottomWidth: index < choicesWithAsk.length - 1 ? 1 : 0,
                          borderBottomColor: colors.border,
                          borderBottomStyle: 'solid',
                          background: isAsk ? `${colors.primary}10` : 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        <span
                          style={{
                            color: isAsk ? colors.primary : colors.textMain,
                            fontSize: 14,
                            fontWeight: isAsk ? '600' : 'normal',
                          }}
                        >
                          {choice}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar overlay */}
        {sidebarOpen && (
          <>
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                zIndex: 9998,
              }}
              onClick={() => setSidebarOpen(false)}
            />
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '85%',
                maxWidth: 320,
                backgroundColor: colors.surface,
                borderRightWidth: 1,
                borderRightColor: colors.border,
                borderRightStyle: 'solid',
                zIndex: 9999,
                display: 'flex',
                flexDirection: 'column',
              }}
              onClick={e => e.stopPropagation()}
            >
              <div
                style={{
                  padding: 16,
                  borderBottomWidth: 1,
                  borderBottomColor: colors.border,
                  borderBottomStyle: 'solid',
                }}
              >
                <span
                  style={{
                    color: colors.primary,
                    fontSize: 24,
                    fontWeight: 'bold',
                    display: 'block',
                    marginBottom: 10,
                  }}
                >
                  {gameState.storyName}
                </span>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ color: colors.textMuted, fontSize: 14 }}>
                    {gameState.bibleVerse}
                  </span>
                  <button
                    onClick={() => setSidebarOpen(false)}
                    style={{ background: 'none', border: 'none', color: colors.textMuted, fontSize: 20, cursor: 'pointer' }}
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
                {/* Donation Button */}
                <button
                  onClick={() => {
                    setSidebarOpen(false);
                    onDonate();
                  }}
                  style={{
                    width: '100%',
                    padding: 12,
                    borderRadius: 8,
                    marginBottom: 24,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: `${colors.primary}15`,
                    borderWidth: 1,
                    borderColor: colors.primary,
                    borderStyle: 'solid',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 24 }}>☕</span>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: 14, fontWeight: 'bold', color: colors.primary }}>
                        Support the Developer
                      </div>
                      <div style={{ fontSize: 12, color: colors.textMuted }}>
                        Help keep this app free
                      </div>
                    </div>
                  </div>
                  <span style={{ fontSize: 24 }}>💜</span>
                </button>

                {/* Story Info */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 8, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 1.5 }}>
                    Story
                  </div>
                  <div style={{ fontWeight: 'bold', marginBottom: 4, fontSize: 14, color: colors.textMain }}>
                    {gameState.storyName || 'Untitled'}
                  </div>
                  <div style={{ fontSize: 14, color: colors.primary }}>
                    {gameState.bibleVerse}
                  </div>
                  <div style={{ fontSize: 14, marginTop: 4, color: colors.textMuted }}>
                    {gameState.history.length} turns
                  </div>
                </div>

                {/* Directives */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 8, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 1.5 }}>
                    Active Directives
                  </div>
                  <textarea
                    value={gameState.directives || ''}
                    readOnly
                    rows={5}
                    style={{
                      width: '100%',
                      padding: 12,
                      borderRadius: 8,
                      fontSize: 14,
                      backgroundColor: colors.background,
                      borderWidth: 1,
                      borderColor: colors.border,
                      borderStyle: 'solid',
                      color: colors.textMain,
                      resize: 'none',
                    }}
                    placeholder="What the AI should do..."
                  />
                </div>
              </div>

              <div style={{ padding: 16, borderTopWidth: 1, borderTopColor: colors.border, borderTopStyle: 'solid' }}>
                <button
                  onClick={onExit}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: 10,
                    backgroundColor: colors.primary,
                    border: 'none',
                    color: '#ffffff',
                    fontSize: 14,
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Save & Exit
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add keyframe animation for spinner */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function AppContent() {
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Crafting your story...');
  const wasLoadingRef = React.useRef(false);
  const [showIntro, setShowIntro] = useState(true);
  const [initialAutosaves, setInitialAutosaves] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showDonation, setShowDonation] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const { currentTheme: colors } = useTheme();
  const { narrativeSettings } = useSettings();
  const { recordTurn, recordVerseExplored, recordWordsWritten, setGameActive } = useAchievements();

  // Session time tracking
  const [sessionSeconds, setSessionSeconds] = useState(0);

  // Track achievements playtime
  useEffect(() => {
    if (gameState?.status === 'playing') {
      setGameActive(true);
      return () => setGameActive(false);
    }
  }, [gameState?.id, gameState?.status, setGameActive]);

  // Session timer
  useEffect(() => {
    const timer = setInterval(() => setSessionSeconds(prev => prev + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  // Update loading message when loading starts
  useEffect(() => {
    if (isLoading && !wasLoadingRef.current) {
      setLoadingMessage(getRandomLoadingMessage());
    }
    wasLoadingRef.current = isLoading;
  }, [isLoading]);

  // Load autosaves on mount
  useEffect(() => {
    listAutosaves().then(saves => setInitialAutosaves(saves || []));
  }, []);

  // Centralized AI call handler
  const getAI = async (state: GameState, history: AIHistoryMessage[]) => {
    const optimizedHistory = state.summary
      ? history.slice(-narrativeSettings.summarizeAfterTurns)
      : history;

    return await fetchAIResponse(
      state.mode,
      state.bibleVerse,
      state.bibleVerseText,
      state.directives || '',
      undefined,
      optimizedHistory,
      narrativeSettings,
      state.summary,
      state.currentFocus
    );
  };

  const generateId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  };

  const handleStartGame = async (initialState: GameState) => {
    const newGameId = initialState.id || generateId();
    let stateWithId = { ...initialState, id: newGameId };
    stateWithId.history = [];
    stateWithId.turnNumber = 0;

    if (!stateWithId.directives && narrativeSettings.defaultDirectives) {
      stateWithId = { ...stateWithId, directives: narrativeSettings.defaultDirectives };
    }

    setGameState(stateWithId);
    setIsLoading(true);

    try {
      const startPromptText = INITIAL_PROMPT(
        initialState.mode,
        initialState.bibleVerse,
        initialState.bibleVerseText
      );

      const formattedHistory: AIHistoryMessage[] = [
        {
          role: 'user',
          parts: [{ text: startPromptText }],
        },
      ];

      const aiRes = await getAI(stateWithId, formattedHistory);

      const firstMsg: Message = {
        id: generateId(),
        role: 'model',
        content: aiRes.storyText,
        suggestedActions: aiRes.suggestedActions,
        relatedPassages: aiRes.relatedPassages,
        currentPassage: aiRes.currentPassage && aiRes.currentPassage !== stateWithId.bibleVerse
          ? aiRes.currentPassage
          : (aiRes.relatedPassages && aiRes.relatedPassages[0] ? aiRes.relatedPassages[0] : undefined),
        timestamp: Date.now(),
        parts: [{ text: JSON.stringify(aiRes) }],
        tokenUsage: aiRes.tokenUsage,
        provider: aiRes.provider,
      };

      setGameState(prev => {
        if (!prev) return null;
        const updatedState = {
          ...prev,
          history: [firstMsg],
          provider: aiRes.provider,
        };
        saveAutosave(updatedState, Math.floor(sessionSeconds / 60));
        recordVerseExplored(prev.bibleVerse);
        return updatedState;
      });
    } catch (err) {
      console.error('Failed to start game:', err);
      setError('Could not connect to the story generator.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (text: string) => {
    if (!gameState || isLoading) return;
    setIsLoading(true);

    // Detect "Explore [verse]" pattern for word studies
    let updatedState = gameState;
    const exploreMatch = text.match(/^Explore\s+(.+)$/i);
    if (exploreMatch && isWordStudyTopic(gameState.bibleVerse)) {
      const verseReference = exploreMatch[1].trim();
      updatedState = { ...gameState, currentFocus: verseReference };
      // Update the state before sending to AI
      setGameState(updatedState);
    }

    const newUserMessage = {
      id: generateId(),
      role: 'user' as const,
      content: text,
      timestamp: Date.now(),
    };

    const currentHistory = [...updatedState.history, newUserMessage];

    const relevantHistoryForAI = currentHistory.map(m => {
      const result: any = {
        role: m.role as 'user' | 'model',
        parts: [{ text: m.content }],
      };
      if ('response_id' in m && m.response_id) {
        result.response_id = m.response_id;
      }
      return result;
    });

    try {
      const aiRes = await getAI(updatedState, relevantHistoryForAI);

      const modelMessage: Message = {
        id: generateId(),
        role: 'model',
        content: aiRes.storyText,
        suggestedActions: aiRes.suggestedActions,
        relatedPassages: aiRes.relatedPassages,
        currentPassage: aiRes.currentPassage && aiRes.currentPassage !== gameState.bibleVerse
          ? aiRes.currentPassage
          : (aiRes.relatedPassages && aiRes.relatedPassages[0] ? aiRes.relatedPassages[0] : undefined),
        timestamp: Date.now(),
        parts: [{ text: JSON.stringify(aiRes) }],
        tokenUsage: aiRes.tokenUsage,
        provider: aiRes.provider,
        response_id: aiRes.response_id,
      };

      setGameState(prev => {
        if (!prev) return null;
        const updatedState = {
          ...prev,
          history: [...currentHistory, modelMessage],
          provider: aiRes.provider,
          turnNumber: prev.turnNumber + 1,
        };
        saveAutosave(updatedState, Math.floor(sessionSeconds / 60));
        recordTurn();
        const wordCount = aiRes.storyText.split(/\s+/).length;
        recordWordsWritten(wordCount);
        return updatedState;
      });
    } catch (err) {
      console.error('Narrative error:', err);
      setError('The narrative was interrupted.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSilentContinue = () => {
    handleSendMessage('');
  };

  const handleRedo = async () => {
    if (!gameState || isLoading) return;
    const newHistory = [...gameState.history];
    if (newHistory.length === 0) return;

    if (newHistory.length === 1 && newHistory[0].role === 'model') {
      await handleSendMessage(gameState.bibleVerse || 'Begin the story');
      return;
    }

    if (newHistory[newHistory.length - 1].role === 'model') {
      newHistory.pop();
      const lastUserMsgIndex = newHistory.map(m => m.role).lastIndexOf('user');
      if (lastUserMsgIndex !== -1) {
        const redoFromHistory = newHistory.slice(0, lastUserMsgIndex + 1);
        const lastUserMsg = redoFromHistory[redoFromHistory.length - 1];

        setGameState(prev => {
          if (!prev) return null;
          const updatedState = { ...prev, history: redoFromHistory };
          saveAutosave(updatedState, Math.floor(sessionSeconds / 60));
          return updatedState;
        });
        await handleSendMessage(lastUserMsg.content);
      } else {
        setGameState(null);
      }
    }
  };

  const handleLoadGame = async (id: string) => {
    setIsLoading(true);
    try {
      const loadedGame = await loadAutosave(id);
      if (loadedGame) {
        setGameState(loadedGame);
      } else {
        setError('Could not load the selected story.');
      }
    } catch (err) {
      console.error('Error loading game:', err);
      setError('Could not load story.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExitGame = async () => {
    setGameState(null);
    const saves = await listAutosaves();
    setInitialAutosaves(saves || []);
  };

  const handleRefreshAutosaves = async () => {
    const saves = await listAutosaves();
    setInitialAutosaves(saves || []);
  };

  return (
    <>
      {/* Background Image - matches mobile ImageBackground */}
      <div className="app-background" />
      {/* Dark Overlay - matches mobile dark overlay */}
      <div className="app-overlay" />
      {/* Main App Container */}
      <div className="app-container">
        {error && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
          <div
            className="bg-surface rounded-lg p-6 max-w-md"
            style={{ backgroundColor: colors.surface }}
          >
            <h2 className="text-xl font-bold mb-4" style={{ color: colors.primary }}>
              Error
            </h2>
            <p className="mb-4" style={{ color: colors.textMain }}>
              {error}
            </p>
            <button
              onClick={() => setError(null)}
              className="px-4 py-2 rounded font-bold"
              style={{ backgroundColor: colors.primary, color: '#fff' }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {showIntro ? (
        <IntroScreen
          onBegin={() => setShowIntro(false)}
          onOpenSettings={() => setShowSettings(true)}
        />
      ) : !gameState ? (
        <SetupWizard
          onStart={handleStartGame}
          onImport={() => {
            /* TODO */
          }}
          autosaves={initialAutosaves || []}
          onLoadGame={handleLoadGame}
          onClose={() => setShowIntro(true)}
          onRefreshAutosaves={handleRefreshAutosaves}
          onOpenSettings={() => setShowSettings(true)}
        />
      ) : (
        <NarrativeUI
          gameState={gameState}
          onSendMessage={handleSendMessage}
          onSilentContinue={handleSilentContinue}
          onRedo={handleRedo}
          onExit={handleExitGame}
          isLoading={isLoading}
          onDonate={() => setShowDonation(true)}
          onOpenSettings={() => setShowSettings(true)}
        />
      )}

      {/* Settings Menu */}
      <SettingsMenu isOpen={showSettings} onClose={() => setShowSettings(false)} onDonate={() => setShowDonation(true)} />

      {/* Donation Modal */}
      {showDonation && <DonationModal onClose={() => setShowDonation(false)} />}
      </div>
    </>
  );
}

export default function App() {
  const [fontsLoaded, setFontsLoaded] = useState(false);

  useEffect(() => {
    // Preload fonts
    const fonts = [
      'Eagle Lake',
      'OpenDyslexic',
      'Lexend',
      'Atkinson Hyperlegible',
    ];

    Promise.all(
      fonts.map(font =>
        document.fonts.load(`16px "${font}"`).catch(() => {
          // Font not available, that's ok
        })
      )
    ).then(() => setFontsLoaded(true));

    // Set fontsLoaded to true after a timeout even if fonts fail to load
    const timeout = setTimeout(() => setFontsLoaded(true), 1000);
    return () => clearTimeout(timeout);
  }, []);

  if (!fontsLoaded) {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        style={{ backgroundColor: '#0a0e27' }}
      >
        <div className="text-2xl font-bold" style={{ color: '#d4af37', fontFamily: 'serif' }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <SettingsProvider>
      <ThemeProvider>
        <FontProvider>
          <AchievementsProvider>
            <AppContent />
          </AchievementsProvider>
        </FontProvider>
      </ThemeProvider>
    </SettingsProvider>
  );
}
